import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { UserRoundCheck } from 'lucide-react';
import type { Customer, CustomerMatchMode } from '@worki/shared';
import { api } from '../lib/api.js';
import { useDebounced } from '../lib/useDebounced.js';
import { cn } from '../lib/cn.js';

/**
 * LEAD-004 — the collision between a phone number that is already on a customer
 * card and a name typed next to it that is not the one on that card.
 *
 * It used to be settled in silence, on the server: the card won, and the name,
 * email and city just typed were dropped. A quote written for one person saved
 * itself under another's name and printed it on the PDF that person received,
 * with nothing on screen ever saying so. This is the screen half of the fix —
 * the number is named out loud while the form is still open, and the answer is
 * the user's rather than ours. `resolveCustomer` is the other half.
 *
 * Shared by the quote editor and the lead form because both type the four
 * customer fields fresh and both write through the same resolve; a second
 * implementation would be a second chance for the two to disagree.
 */

/** What the user answered. `keep` is the old behaviour, now said out loud. */
export type CustomerMatchChoice = 'keep' | 'update' | 'new';

export const MATCH_MODE: Record<CustomerMatchChoice, CustomerMatchMode> = {
  keep: 'keep',
  update: 'update',
  new: 'new',
};

/** Compared loosely: trailing spaces and capitals are not a different person. */
const sameName = (a: string, b: string) =>
  a.trim().replace(/\s+/g, ' ').toLowerCase() === b.trim().replace(/\s+/g, ' ').toLowerCase();

export interface CustomerMatch {
  /** The stored card behind the typed number, once there is enough to look up. */
  match: Customer | null;
  /** A card was found and it disagrees about the name — the case worth asking about. */
  conflict: boolean;
  choice: CustomerMatchChoice | null;
  choose: (choice: CustomerMatchChoice) => void;
  /** What to send: `auto` while there is nothing to decide. */
  mode: CustomerMatchMode;
  /** True once a save was refused for want of an answer. */
  missing: boolean;
  /** Call before saving; false means stop and let the notice be answered. */
  settle: () => boolean;
}

export function useCustomerMatch(phone: string, name: string): CustomerMatch {
  const [choice, setChoice] = useState<CustomerMatchChoice | null>(null);
  const [missing, setMissing] = useState(false);

  // Debounced so a number is looked up once it is typed rather than per digit.
  const settled = useDebounced(phone.trim(), 400);
  // Seven is the shortest Israeli landline without an area code; below it every
  // number in the book matches, and the lookup would only ever be noise.
  const enough = settled.replace(/\D/g, '').length >= 7;

  const lookup = useQuery({
    queryKey: ['customer-lookup', settled],
    queryFn: () => api.get<{ customer: Customer | null }>(`/customers/lookup?phone=${encodeURIComponent(settled)}`),
    enabled: enough,
    staleTime: 60_000,
  });

  const match = enough ? (lookup.data?.customer ?? null) : null;
  const conflict = Boolean(match && name.trim() && !sameName(match.name, name));

  // A different card is a different question, so a previous answer to the old
  // one must not carry over into it.
  useEffect(() => {
    setChoice(null);
    setMissing(false);
  }, [match?.id]);

  return {
    match,
    conflict,
    choice,
    choose: (next) => {
      setChoice(next);
      setMissing(false);
    },
    mode: conflict && choice ? MATCH_MODE[choice] : 'auto',
    missing,
    settle: () => {
      if (!conflict || choice) return true;
      setMissing(true);
      return false;
    },
  };
}

/** Renders nothing until there is something to ask. */
export function CustomerMatchNotice({ state, name }: { state: CustomerMatch; name: string }) {
  const { t } = useTranslation();
  const { match, conflict, choice, choose, missing } = state;
  if (!match || !conflict) return null;

  const option = (value: CustomerMatchChoice, label: string) => (
    <button
      type="button"
      onClick={() => choose(value)}
      aria-pressed={choice === value}
      className={cn(
        'h-8 px-2.5 rounded-lg text-xs font-medium transition-colors',
        choice === value ? 'bg-primary text-primary-foreground' : 'bg-card hover:bg-muted',
      )}
    >
      {label}
    </button>
  );

  const hint = choice ? t(`customer.match.${choice}Hint`, { name: match.name }) : null;

  return (
    <div
      role="status"
      className={cn(
        'rounded-xl border px-3 py-2.5 text-sm space-y-2',
        missing ? 'border-destructive/50 bg-destructive/10' : 'border-warning/40 bg-warning/10',
      )}
    >
      <p className="flex items-start gap-2">
        <UserRoundCheck className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
        <span className="min-w-0">
          {t('customer.match.body', { phone: match.phone ?? '', name: match.name })}
        </span>
      </p>
      <div className="flex flex-wrap gap-1.5">
        {option('update', t('customer.match.update', { name: name.trim() }))}
        {option('keep', t('customer.match.keep', { name: match.name }))}
        {option('new', t('customer.match.new'))}
      </div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      {missing && <p className="text-xs font-medium text-destructive">{t('customer.match.required')}</p>}
    </div>
  );
}
